import dayjs from "dayjs";
import { DateInputDiv } from "../../../app/newLayout/page/Reports/components/MenuAction/Automate.styles";
import { DatePicker } from "antd";
import { getIn } from "formik";
import { SectionDescription } from "../typography";
import {
  FileInputSpinner,
  StyledFormFileInput,
  StyledFormInput,
  StyledFormInputCheckbox,
  StyledTextInput,
} from "./Input.styles";
import { Upload } from "../../../assets/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMemo } from "react";
import { clockDatePickerProps } from "../../molecules/Input/ClockZoneControl";
import {
  DATE_TIME_PICKER_FORMAT,
  clockToIsoInZone,
  isoToClockDayjs,
  toCalendarDate,
} from "../../../helper/dateTime";
import { usePlaceTimezone } from "../../../helper/usePlaceTimezone";

export const TextInput = ({ value, onChange, onClick, onKeyDown, placeholder, ...props }) => {
  return (
    <StyledTextInput
      {...props}
      placeholder={placeholder}
      type="text"
      value={value}
      onChange={onChange}
      onClick={onClick}
      onKeyDown={onKeyDown}
    />
  );
};

export const Checkbox = ({ checked, onChange, value, name, ...props }) => {
  return (
    <input
      {...props}
      checked={checked}
      name={name}
      type="checkbox"
      value={value}
      onChange={onChange}
    />
  );
};

export const CheckboxV1 = ({ checked, onChange, value, name, ...props }) => {
  return (
    <StyledFormInputCheckbox
      {...props}
      checked={checked}
      name={name}
      type="checkbox"
      value={value}
      onChange={onChange}
    />
  );
};

export const FormInput = ({ variant, ...props }) => {
  return <StyledFormInput $variant={variant} {...props} />;
};

export const DateInput = ({
  value,
  inputName,
  handleDateInput,
  widthOfDatePicker,
  showTime,
  siteTimezone,
  companyTimezone,
  timeZone: timeZoneProp,
  ...props
}) => {
  const isClock = Boolean(showTime);
  const placeZone = usePlaceTimezone({ siteTimezone, companyTimezone });
  const [zone, setZone] = useState(timeZoneProp || placeZone);

  useEffect(() => {
    if (!timeZoneProp) {
      setZone(placeZone);
    }
  }, [placeZone, timeZoneProp]);

  const dateValue = useMemo(() => {
    if (!value) {
      return null;
    }
    if (isClock) {
      return isoToClockDayjs(value, zone);
    }
    const day = toCalendarDate(value);
    return dayjs(day || value);
  }, [value, isClock, zone]);

  const handleChange = val => {
    if (!val) {
      handleDateInput(inputName, val);
      return;
    }
    handleDateInput(inputName, isClock ? clockToIsoInZone(val, zone) : val.format("YYYY-MM-DD"));
  };

  const handleZoneChange = nextZone => {
    setZone(nextZone);
    if (isClock && dateValue) {
      handleDateInput(inputName, clockToIsoInZone(dateValue, nextZone));
    }
  };

  return (
    <DateInputDiv width={widthOfDatePicker}>
      <DatePicker
        {...props}
        allowClear={false}
        onChange={handleChange}
        showTime={
          isClock ? (typeof showTime === "object" ? showTime : { format: "HH:mm" }) : showTime
        }
        value={dateValue}
        {...(isClock
          ? clockDatePickerProps({
              at: dateValue,
              disabled: props.disabled,
              format: DATE_TIME_PICKER_FORMAT,
              onChange: handleZoneChange,
              timeZone: zone,
            })
          : {})}
      />
    </DateInputDiv>
  );
};

export const FormFileInput = ({
  formik: {
    handleBlur,
    isSubmitting,
    setFieldError,
    setFieldTouched,
    setFieldValue,
    submitForm,
    validateField,
    validateForm,
    values,
  },
  name,
  accept = ".csv,.xlsx",
  disabled = false,
  isLoading = false,
  loadingPlaceholder = "Your file is uploading. Please wait...",
  placeholder = "Drag & drop or click to upload files",
  ...props
}) => {
  const inputRef = useRef(null);

  const [isActive, setActive] = useState(false);

  const openPicker = () => inputRef.current?.click();

  const setFile = useCallback(
    file => {
      setFieldValue(name, file);
      setFieldTouched(name, true, false);
    },
    [name, setFieldTouched, setFieldValue],
  );

  const runValidateAndMaybeSubmit = useCallback(async () => {
    // Validate only this field first (fast)
    await validateField(name);

    // Get fresh errors after validation
    const allErrors = await validateForm();
    const fieldError = getIn(allErrors, name);

    if (!fieldError && !isSubmitting) {
      await submitForm();
    }
  }, [isSubmitting, name, submitForm, validateField, validateForm]);

  const handleDrop = useCallback(
    async event => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;

      setActive(false);

      const file = event.dataTransfer.files?.[0] || null;

      if (file) setFieldError(name, undefined);
      setFile(file);

      await runValidateAndMaybeSubmit();
    },
    [disabled, name, runValidateAndMaybeSubmit, setFieldError, setFile],
  );

  const handleKeyDown = event => {
    if (event.key === "Enter" || event.key === " ") {
      openPicker();
    }
  };

  const peformDefault = event => {
    event.preventDefault();
    setActive(true);
  };

  const manageBlur = event => {
    handleBlur(event);
    setFieldTouched(name, true, false);
  };

  const handleChange = useCallback(
    async event => {
      const file = event.currentTarget.files?.[0] ?? null;

      // allow re-selecting the same file
      event.currentTarget.value = "";

      if (file) setFieldError(name, undefined);

      // put into Formik state + mark touched
      setFile(file);

      await runValidateAndMaybeSubmit();
    },
    [name, runValidateAndMaybeSubmit, setFieldError, setFile],
  );

  const value = values[name];

  return (
    <>
      <StyledFormFileInput
        $active={isActive}
        aria-label="Upload file"
        aria-disabled={disabled}
        role="button"
        tabIndex={0}
        onClick={openPicker}
        onDragEnter={peformDefault}
        onDragLeave={() => setActive(false)}
        onDragOver={peformDefault}
        onDrop={handleDrop}
        onKeyDown={handleKeyDown}
      >
        {isLoading ? (
          <>
            <FileInputSpinner />
            <SectionDescription className="input" desc={loadingPlaceholder} />
          </>
        ) : (
          <>
            <Upload />
            <SectionDescription className="input" desc={placeholder} />
          </>
        )}
        <input
          {...props}
          accept={accept}
          disabled={disabled}
          name={name}
          ref={inputRef}
          style={{ display: "none" }}
          type="file"
          onBlur={manageBlur}
          onChange={handleChange}
        />
      </StyledFormFileInput>
      {value && <SectionDescription className="input file-info" desc={value.name} />}
    </>
  );
};
